import type {
	GamingConsoleSettings,
	PCSettings,
	RelationQuery,
	SellerOutput,
} from "@/api/custom_types";
import { useApiError } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import AddPaymentDialog from "@/pages/payment/shared/AddPaymentDialog";
import RelationNameLink from "@/pages/relations/RelationNameLink";
import { useGetAggregatedSellers } from "@/services";
import { formatNumber } from "@/utils/tools";
import {
	type StatusTranslation,
	getRelationQueryTranslation,
} from "@/utils/translations";
import {
	Chip,
	type ChipVariant,
	DataTable,
	Icon,
	Typography,
	useServerTableState,
} from "@st-marketplace/ui";
import type { ColumnDef } from "@tanstack/react-table";
import { type FC, useCallback, useMemo, useState } from "react";
import {
	RelationsAddRequest,
	RelationsFilterForm,
	RelationsPendingRequests,
} from "./components";
import type { FilterFormData } from "./types";

function isPlayStation(
	p: GamingConsoleSettings | PCSettings,
): p is GamingConsoleSettings & { type_: "ps" } {
	return p.type_ === "ps";
}

const ACTIVE_OR_BANNED = [
	"ACTIVE",
	"BANNED",
] as const satisfies RelationQuery[];

const normalizeStatuses = (
	statuses: RelationQuery[] | undefined,
): RelationQuery[] | undefined => {
	if (!statuses?.length) return undefined;

	const hasActive = statuses.includes("ACTIVE" as RelationQuery);

	const normalized = hasActive
		? Array.from(
				new Set(
					statuses.map((s) =>
						s === ("ACTIVE" as RelationQuery) ? "ACCEPTED" : s,
					),
				),
			)
		: statuses;

	return normalized.length ? normalized : undefined;
};

const getPsSetting = (platform: Array<GamingConsoleSettings | PCSettings>) => {
	return platform.find(isPlayStation);
};

const STATUS_COLOR_TO_CHIP_VARIANT: Record<
	StatusTranslation["color"],
	ChipVariant
> = {
	success: "success",
	primary: "info",
	error: "error",
	warning: "warning",
};

const SellerRelations: FC = () => {
	const { t } = useTranslation();
	const { getErrorInterface } = useApiError();

	const server = useServerTableState();

	const [filters, setFilters] = useState<FilterFormData>({
		buyer_name: null,
		seller_name: null,
		status: [...ACTIVE_OR_BANNED],
	});

	const handleSearch = useCallback(
		(values: FilterFormData) => {
			setFilters(values);
			server.onPageChange(0);
		},
		[server],
	);

	const {
		data: sellers,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useGetAggregatedSellers({
		query: {
			page: server.pageIndex + 1,
			page_size: server.pageSize,
			statuses: normalizeStatuses(filters.status as RelationQuery[]),
			seller_name: filters.seller_name,
		},
	});

	const columns = useMemo<ColumnDef<SellerOutput>[]>(
		() => [
			{
				id: "seller_name",
				header: t("relation.tableSellerName"),
				enableSorting: false,
				cell: ({ row }) => (
					<RelationNameLink
						name={row.original.seller_display_name}
						relationId={row.original.relation_id}
						party="seller"
					/>
				),
			},
			{
				id: "status",
				header: t("relation.tableStatus"),
				enableSorting: false,
				cell: ({ row }) => {
					const { state } = row.original;
					const { color, text } = getRelationQueryTranslation(state, t);
					return (
						<Chip variant={STATUS_COLOR_TO_CHIP_VARIANT[color]} size="sm">
							{text}
						</Chip>
					);
				},
			},
			{
				id: "allow_to_order",
				header: t("relation.tableAllowedToOrder"),
				enableSorting: false,
				cell: ({ row }) => {
					const { platform } = row.original;
					const psSetting = getPsSetting(platform);
					const isAllowed = psSetting?.allow_to_order;

					return isAllowed ? (
						<Icon name="check-line" size="sm" className="text-success-text" />
					) : (
						<Icon name="close-line" size="sm" className="text-danger-text" />
					);
				},
			},
			{
				id: "available_stock",
				header: t("relation.tableAvailableStock"),
				enableSorting: false,
				cell: ({ row }) => {
					const { platform } = row.original;
					const psSetting = getPsSetting(platform);
					const availableStock = psSetting?.available_coins?.value || 0;
					return formatNumber(availableStock, "count");
				},
			},
			{
				id: "unit_price",
				header: t("relation.tableUnitPrice"),
				enableSorting: false,
				cell: ({ row }) => {
					const { platform } = row.original;
					const psSetting = getPsSetting(platform);
					const unitPrice = psSetting?.unit_price;
					return formatNumber(unitPrice, "price");
				},
			},
			{
				id: "debt",
				header: t("relation.tableDebt"),
				enableSorting: false,
				cell: ({ row }) => {
					const { current_debt } = row.original;
					return formatNumber(current_debt, "price");
				},
			},
			{
				id: "minimum_to_order_ps",
				header: t("relation.tableMinimumToOrderPs"),
				enableSorting: false,
				cell: ({ row }) => {
					const { platform } = row.original;
					const psSetting = getPsSetting(platform);
					const minimumToOrder = psSetting?.minimum_to_order;
					return formatNumber(minimumToOrder, "count");
				},
			},
			{
				id: "maximum_to_order_ps",
				header: t("relation.tableMaximumToOrderPs"),
				enableSorting: false,
				cell: ({ row }) => {
					const { platform } = row.original;
					const psSetting = getPsSetting(platform);
					const maximumToOrder = psSetting?.maximum_to_order;
					return formatNumber(maximumToOrder, "count");
				},
			},
			{
				id: "maximum_debt",
				header: t("relation.tableMaximumDebt"),
				enableSorting: false,
				cell: ({ row }) => {
					const { max_debt } = row.original;
					return formatNumber(max_debt, "price");
				},
			},
		],
		[t],
	);

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center justify-between">
				<Typography variant="title-md" as="h1">
					{t("dashboard.menuSellerRelations")}
				</Typography>
				<div className="flex flex-col-reverse items-stretch justify-end gap-2 md:flex-row md:items-center">
					<RelationsPendingRequests relationRole="seller" />

					<RelationsAddRequest
						relationRole="seller"
						buttonLabel={t("relation.addSellerButton")}
						dialogTitle={t("relation.addSellerButton")}
					/>

					<AddPaymentDialog direction="send" />
				</div>
			</div>

			<RelationsFilterForm onSearch={handleSearch} isSeller />

			<DataTable
				columns={columns}
				data={sellers?.results ?? []}
				totalItems={sellers?.count ?? 0}
				server={server}
				isLoading={isLoading}
				isFetching={isFetching}
				error={error ? getErrorInterface(error) : null}
				onRetry={refetch}
				getRowId={(row) => row.seller_id}
			/>
		</div>
	);
};

export default SellerRelations;
